// frontend/src/admin/pages/AdminTicketsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { listAdminTickets } from "../api";

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/**
 * AdminTicketsPage — operator console listing every org's support tickets with
 * the user's question, status, tier, timestamps, and SLA state (M4a-A7).
 */
function AdminTicketsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [overdue, setOverdue] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { per_page: 100 };
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (overdue) params.overdue = true;
      const data = await listAdminTickets(params);
      setRows(data.items ?? []);
    } catch {
      toast.error("Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, priority, overdue]);

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-5">Support Tickets</h1>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Status</span>
          <select
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Priority</span>
          <select
            aria-label="Filter by priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            aria-label="Overdue only"
            checked={overdue}
            onChange={(e) => setOverdue(e.target.checked)}
          />
          Overdue only
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No tickets found.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b">
              <th className="py-2 pr-2">Org</th>
              <th className="py-2 pr-2">User</th>
              <th className="py-2 pr-2">Question</th>
              <th className="py-2 pr-2">Category</th>
              <th className="py-2 pr-2">Priority</th>
              <th className="py-2 pr-2">Status</th>
              <th className="py-2 pr-2">Tier</th>
              <th className="py-2 pr-2">Created</th>
              <th className="py-2 pr-2">Last response</th>
              <th className="py-2 pr-2">SLA due</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/admin/tickets/${t.id}`)}
                className={[
                  "border-b last:border-0 cursor-pointer hover:bg-slate-50",
                  t.sla_breached ? "bg-rose-50" : "",
                ].join(" ")}
              >
                <td className="py-2 pr-2 text-slate-600">{t.org_name}</td>
                <td className="py-2 pr-2 text-slate-600">{t.user_email}</td>
                <td className="py-2 pr-2 font-medium text-slate-800">{t.subject}</td>
                <td className="py-2 pr-2 text-slate-500">{t.category}</td>
                <td className="py-2 pr-2 text-slate-500">{t.priority}</td>
                <td className="py-2 pr-2 text-slate-500">{t.status}</td>
                <td className="py-2 pr-2 text-slate-500">{t.plan_tier}</td>
                <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">
                  {fmt(t.created_at)}
                </td>
                <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">
                  {fmt(t.last_message_at)}
                </td>
                <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">
                  {fmt(t.sla_due_at)}
                </td>
                <td className="py-2">
                  {t.sla_breached && (
                    <span className="text-xs font-semibold text-rose-600">
                      ⚠️ Overdue
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminTicketsPage;
